<script lang="ts" setup>
import { ref, computed, watch } from "vue";

import NumberAnimation from "vue-number-animation";
import { getWeiiScore } from "@/services/api/chart.api";
import { getParisProofColorClass } from "@/composables/colors";
import EiffelTower from "@/components/general/EiffelTower.vue";
import { formatInt } from "@/composables/formatNumbers";
import { useToast } from "vue-toastification";
import { useUserStore } from "@/stores/user";

const toaster = useToast();
const userStore = useUserStore();
const props = defineProps({
  token: {
    type: String,
  },
  refDate: {
    type: Date,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const weiiSummary = ref({
  meanScore: 0,
  meanNorm: 0,
  count: 0,
  total: 0,
  percentage: 0,
});
const isLoading = ref(false);
const isLoaded = ref(false);

const hasData = computed(() => isLoaded.value && weiiSummary.value && !Number.isNaN(weiiSummary.value.meanScore) && !Number.isNaN(weiiSummary.value.meanNorm) && weiiSummary.value.count > 0);

watch(
  () => props.token,
  () => {
    fetchData();
  },
  { immediate: true }
);

async function fetchData() {
  isLoaded.value = false;
  if (props.token) {
    isLoading.value = true;
    const response = await getWeiiScore(props.token, null, userStore.currentPartyReference);
    if (response) {
      weiiSummary.value.meanScore = response.data.meanScore;
      weiiSummary.value.meanNorm = response.data.meanNorm;
      weiiSummary.value.count = response.data.count;
      weiiSummary.value.total = response.data.total;
      weiiSummary.value.percentage = response.data.percentage;
      isLoaded.value = true;
    } else {
      console.error("Failed to get WEii-score summary");
      toaster.error("Ophalen van de WEii-scores is mislukt!");
    }
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="app-paris-proof-sidebar" :class="{ 'app-paris-proof-sidebar--compact': props.compact }">
    <header>
      <h2>WEii-score</h2>
      <div class="widget-actions">
        <information-dropdown>
          <template v-slot:header>
            <h2>Paris Proof?</h2>
          </template>

          <p>U ziet hier uw gewogen WEii 3 Score op basis van de WEii methodiek van al uw gebouwen.</p>

          <p>
            WEii staat voor Werkelijke Energie intensiteit indicator en is een gestandaardiseerde methodiek voor het bepalen van een energie-efficiëntie indicator op basis van het werkelijke
            energiegebruik van een gebouw.
          </p>

          <p>
            In tegenstelling tot NTA 8800 (BENG) is WEii gebaseerd op het werkelijke energiegebruik van een gebouw in gebruik en niet op een berekend gebouwgebonden energiegebruik. Er zijn
            verschillende gebouwtype en klassen ontwikkeld, daarbij is de klasse Paris Proof een gebouw dat voldoet aan de doelstellingen voor 2050.
          </p>

          <p>WEii-score is berekend op basis van het WEii protocol versie 3.0.</p>

          <h3>Over Paris Proof</h3>
          <p>
            <strong>Paris Proof</strong> is gebaseerd op de door Dutch Green Building Council opgestelde doelwaarden voor het realiseren van de 2050-doelstelling van het akkoord van Parijs.
            Uitgangspunt bij de getalswaarde van de Paris Proof doelstelling per gebouwtype is dat met de in 2050 verwachte beschikbare hoeveelheid duurzame energie alle gebouwen van energie
            kunnen worden voorzien als de gebouwen een energiegebruik hebben dat maximaal gelijk is aan de Paris Proof getalswaarden. Daarmee zou voldaan worden aan de doelselling van het
            akkoord van Parijs en het Nederlandse klimaatakkoord.
          </p>

          <p>
            Klik
            <a class="nav-link-light" href="https://www.innax.nl/kennisbank/toekomstvaste-gebouwen/paris-proof" target="_blank"><b>hier</b></a>
            voor een overzicht van de normering.
          </p>
        </information-dropdown>
      </div>
    </header>
    <template v-if="props.compact">
      <div class="compact-layout">
        <div class="compact-layout__visual">
          <eiffel-tower :score="weiiSummary.meanScore" :norm="weiiSummary.meanNorm"></eiffel-tower>
        </div>
        <div class="compact-layout__content">
          <h5 v-if="isLoaded && weiiSummary.count != 0 && weiiSummary.count != weiiSummary.total">Gebaseerd op {{ weiiSummary.count }} van de {{ weiiSummary.total }} gebouwen</h5>
          <h5 v-if="isLoaded && weiiSummary.count != 0 && weiiSummary.count == weiiSummary.total">Gebaseerd op alle gebouwen</h5>

          <div v-if="isLoading === true">
            <loader-spinner :is-loading="isLoading"></loader-spinner>
          </div>

          <div v-else>
            <p v-if="isLoaded && weiiSummary.count == 0">Er zijn nog geen panden waar de WEii-score voor berekend kan worden, doordat er energiedata en/of oppervlaktes missen</p>

            <p v-else class="paris-score">
              <span v-if="hasData" :class="getParisProofColorClass(weiiSummary.meanScore, weiiSummary.meanNorm)">
                Gemiddelde score:
                <number-animation :from="0" :to="weiiSummary.meanScore" :duration="1.6" :format="formatInt"></number-animation>&nbsp;kWh/m²<br />
                Gemiddelde norm: {{ weiiSummary.meanNorm }}&nbsp;kWh/m²
              </span>
              <span v-else :class="getParisProofColorClass(weiiSummary.meanScore, weiiSummary.meanNorm)">
                Gemiddelde score: -<br />
                Gemiddelde norm: -
              </span>
            </p>
          </div>

          <div class="paris-proof-legend">
            <div class="item"><span class="color color-red"></span> Onvoldoende score</div>
            <div class="item"><span class="color color-orange"></span> Matige score</div>
            <div class="item"><span class="color color-green"></span> Onder of gelijk aan de norm</div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <h5 v-if="isLoaded && weiiSummary.count != 0 && weiiSummary.count != weiiSummary.total">Gebaseerd op {{ weiiSummary.count }} van de {{ weiiSummary.total }} gebouwen</h5>
      <h5 v-if="isLoaded && weiiSummary.count != 0 && weiiSummary.count == weiiSummary.total">Gebaseerd op alle gebouwen</h5>

      <v-container v-if="isLoading === true">
        <loader-spinner :is-loading="isLoading"></loader-spinner>
      </v-container>

      <v-container v-else>
        <p v-if="isLoaded && weiiSummary.count == 0">Er zijn nog geen panden waar de WEii-score voor berekend kan worden, doordat er energiedata en/of oppervlaktes missen</p>

        <p v-else class="paris-score">
          <span v-if="hasData" :class="getParisProofColorClass(weiiSummary.meanScore, weiiSummary.meanNorm)">
            Gemiddelde score:
            <number-animation :from="0" :to="weiiSummary.meanScore" :duration="1.6" :format="formatInt"></number-animation>&nbsp;kWh/m²<br />
            Gemiddelde norm: {{ weiiSummary.meanNorm }}&nbsp;kWh/m²
          </span>
          <span v-else :class="getParisProofColorClass(weiiSummary.meanScore, weiiSummary.meanNorm)">
            Gemiddelde score: -<br />
            Gemiddelde norm: -
          </span>
        </p>
      </v-container>
      <v-container class="pa-0">
        <v-row>
          <v-col md="12" sm="6">
            <div class="paris-proof-score">
              <eiffel-tower :score="weiiSummary.meanScore" :norm="weiiSummary.meanNorm"></eiffel-tower>
            </div>
          </v-col>
          <v-col md="12" sm="6">
            <div class="paris-proof-legend">
              <div class="item"><span class="color color-red"></span> Onvoldoende score</div>
              <div class="item"><span class="color color-orange"></span> Matige score</div>
              <div class="item"><span class="color color-green"></span> Onder of gelijk aan de norm</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.app-paris-proof-sidebar--compact {
  height: 100%;
  min-height: 0;
}

.compact-layout {
  display: flex;
  gap: 12px;
  align-items: stretch;
  height: 100%;
  min-height: 0;
}

.compact-layout__visual {
  flex: 0 0 40%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.compact-layout__visual :deep(svg) {
  width: 100%;
  max-width: 150px;
  max-height: 220px;
  min-width: 0;
}

.compact-layout__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.app-paris-proof-sidebar--compact .paris-score {
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.app-paris-proof-sidebar--compact h5 {
  margin-bottom: 10px;
}

.app-paris-proof-sidebar--compact .paris-proof-legend {
  margin-top: 0;
}

.app-paris-proof-sidebar--compact .paris-proof-legend .item {
  margin-top: 8px;
}
</style>
